/**
 * Account-root trial read surface (backend `TrialAccountController`, sales-trials.md "Countdown
 * visibility"). READ-ONLY for the account root (R1 — start/extend/end live only on `/ops`). Goes through
 * `@repo/api-client`'s fetch mutator; shapes mirror the controller DTOs (declared locally until the
 * OpenAPI-generated models land via `just gen`).
 */
import { customInstance } from "@repo/api-client";
import { useQuery } from "@tanstack/react-query";

export type TrialState = "REQUESTED" | "ACTIVE" | "CONVERTED" | "EXPIRED" | "ENDED_EARLY";

export interface TrialBanner {
  trialId: string;
  state: TrialState;
  grantedFlags: string[];
  requestedAtSec: number;
  activationDueAtSec: number;
  activatedAtSec: number | null;
  endsAtSec: number;
  remainingSeconds: number;
}

export interface ConversionPreview {
  plan: string;
  grantedFlags: string[];
  planFlags: string[];
  lostInUseFlags: string[];
}

const base = "/api/v1/billing";

export interface TrialStartedResponse {
  trialId: string;
}

export const trialApi = {
  current: (signal?: AbortSignal) =>
    customInstance<TrialBanner | null>(`${base}/trial`, { method: "GET", signal }),

  /** Start the account's self-serve free trial (account-root). Flips each venue PREVIEW → TRIALING so
   *  the subscription go-live gate is satisfied. 403 if the caller isn't the account root. */
  startSelfServe: () => customInstance<TrialStartedResponse>(`${base}/trial`, { method: "POST" }),

  conversionPreview: (venueId: string, planCode: string) =>
    customInstance<ConversionPreview>(`${base}/trial/conversion-preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ venueId, planCode }),
    }),
};

export const trialKeys = { current: () => ["billing", "trial", "current"] as const };

/** The account's current REQUESTED/ACTIVE trial for the banner; refetches every minute so the clock stays live. */
export function useCurrentTrial(enabled: boolean) {
  return useQuery<TrialBanner | null>({
    queryKey: trialKeys.current(),
    queryFn: ({ signal }) => trialApi.current(signal),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
